import type {
  CourseRecommendationRow,
  EmployeeProteinTarget,
  RecommendableCourseKey,
  SetProteinTarget,
} from "@care-by-ella/contracts";
import { API_BASE, apiFetch, tenantHeader, withCredentials } from "./api";
import { jsonOr401 } from "./clinician-api";

// TES-1067 — the care team's side of the "Stöd → Kost" courses: recommend a
// course to her, and read or set her protein target. Same session-cookie
// client shape as wellness-program-api.ts.

export class NutritionCoursesApiError extends Error {}

function commonHeaders(): Record<string, string> {
  return { "Content-Type": "application/json", ...tenantHeader() };
}

async function jsonOrError<T>(res: Response): Promise<T> {
  if (res.status === 401 || res.status === 403) return jsonOr401<T>(res);
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const body = (await res.json()) as { message?: unknown; error?: unknown };
      const msg = body.message ?? body.error;
      if (typeof msg === "string" && msg.trim()) detail = msg;
    } catch {
      // non-JSON body — keep the status-code detail
    }
    throw new NutritionCoursesApiError(detail);
  }
  return (await res.json()) as T;
}

const base = (patientId: string) =>
  `${API_BASE}/employee-api/v1/patients/${patientId}`;

export async function listCourseRecommendations(
  patientId: string,
): Promise<CourseRecommendationRow[]> {
  const res = await apiFetch(`${base(patientId)}/course-recommendations`, {
    headers: commonHeaders(),
    ...withCredentials,
  });
  return jsonOrError(res);
}

export async function recommendCourse(
  patientId: string,
  courseKey: RecommendableCourseKey,
): Promise<CourseRecommendationRow[]> {
  const res = await apiFetch(`${base(patientId)}/course-recommendations`, {
    method: "POST",
    headers: commonHeaders(),
    body: JSON.stringify({ courseKey }),
    ...withCredentials,
  });
  return jsonOrError(res);
}

export async function getProteinTarget(
  patientId: string,
): Promise<EmployeeProteinTarget> {
  const res = await apiFetch(`${base(patientId)}/protein-target`, {
    headers: commonHeaders(),
    ...withCredentials,
  });
  return jsonOrError(res);
}

export async function setProteinTarget(
  patientId: string,
  body: SetProteinTarget,
): Promise<EmployeeProteinTarget> {
  const res = await apiFetch(`${base(patientId)}/protein-target`, {
    method: "PUT",
    headers: commonHeaders(),
    body: JSON.stringify(body),
    ...withCredentials,
  });
  return jsonOrError(res);
}
